import {FileToUpload, FileUploadStatus} from "../models/fileToUpload";
import {UploadToS3, UploadToS3Config, UploadToS3Result} from "./uploadToS3";
import WCCResourcedError from "../../../../models/wccResourcedError";
import {
    apiMultiPartFileCompleteRequestData,
    apiMultiPartFileCompleteResponseData,
    apiMultiPartFileInitRequestData,
    apiMultiPartFileInitResponseData
} from "../../../../models/apiS3MultiPartFileData";
import {ILogsContainer} from "../../../../logsContainer";
import {UserLogsService} from "../../../../services/userLogs";
import {UploadCancelledError, UploadPausedError, UploadTimeoutError} from "../models/errorTypes";
import {SpeedoMeter} from "../../../../helpers/speedoMeter";

/**
 * The S3 multipart uploader - It uploads a file to AWS S3 by dividing it into smaller chunks. It does this in 3 steps:
 * 1. Creates the associated user file in the db and marks the start of the upload operation. THIS HAPPENS ON THE SERVER.
 * 2. Uploads all the chunks to AWS S3. THIS HAPPENS ON THE CLIENT SIDE.
 * 3. Marks the completion of the file upload after all the chunks are uploaded. THIS HAPPENS ON THE SERVER.
 */
export class UploadToS3MultiPart extends UploadToS3 {

    /**
     * The number of times the uploader should re-attempt a failed file
     * @private
     */
    private static MaxRetryCount = 3;

    /**
     * The time to wait before we retry the upload
     * @private
     */
    private static RetryIntervalInSecs = 10;
    
    /**
     * The timeout for xhr request for each chunk upload
     * @private
     */
    private _xhrTimeout = 50000; //in milliseconds (or 50 seconds)

    /**
     * Tracks the retry attempt counts of each failed part
     * @private
     */
    private _partsUploadAttemptsCount: Map<number, number> = new Map<number, number>();

    /**
     * The current part being uploaded. Init to 1
     * @private
     */
    private _currentPartBeingUploaded = 1;
    
    /**
     * The file data after the user file is created in the db, and the upload is marked as 'Started' on Aws
     * We save it at the class level so that it is not obtained again in case of pause/resume
     * @private
     */
    private _initFileData?: apiMultiPartFileInitResponseData;

    /**
     * Logs for this upload
     * @private
     */
    private _userLogId: string = '';

    /**
     * Track the eTags returned by AWS after the uploading of each chunk
     */
    private _eTags: string[] = [];

    /**
     * Keeps track of how many log entries are there before upload starts
     * This is helpful because if the upload is cancelled and tried again, previous logs are cleared BUT not those logs which existed before the upload process
     * @private
     */
    private _logsCountBeforeUpload: number = 0;

    /**
     * Number of times the upload was paused/resumed
     * @private
     */
    private _pausedCount: number = 0;

    /**
     * Number of times the upload timed out
     * @private
     */
    private _timedOutCount: number = 0;
    
    /**
     * True if pausing and resuming the upload operation is supported; False otherwise
     * We must implement pauseUpload() and resumeUpload() functions if this is set to true
     */
    public override readonly supportsPauseAndResume: boolean = true;
    
    /**
     * Initialises the S3 multipart upload operation
     * @param file The file to upload
     * @param config The S3 multipart upload operation config
     * @param logs The logs container
     * @param userLogsService The user logs service to send logs to the server
     */
    constructor(file: FileToUpload, config: UploadToS3Config, logs: ILogsContainer, userLogsService: UserLogsService) {
        super(file, config, logs, userLogsService);
    }

    /**
     * Triggers the S3 multipart upload operation
     */
    override async upload(): Promise<void> {
        if (this._config.Events?.OnFileUploadStarting) {
            this._config.Events.OnFileUploadStarting();
        }
        
        if (this.abortController == null) {
            this.abortController = new AbortController(); //initialize cancellation token controller
        }
        
        this.changeCanBeDeleted(false);
        
        this._logsCountBeforeUpload = this._logs.logsCount();
        
        //If the user log doesn't exist, create it first
        if (system.isNullOrEmpty(this._userLogId)) {
            try {
                const logCreationRequest = await this._userLogsService.createLogs(settings.discussionId);
                this._userLogId = logCreationRequest.UserLogId;
            } catch (error: any) {
                this.changeCanBeDeleted(true);
                system.showErrorMessage(messages.ThereWasASystemError);

                if (this._config.Events?.OnFileUploadComplete) {
                    this._config.Events?.OnFileUploadComplete();
                }
                
                return;
            }
        }
        
        /* ##################
                STEP 1
           #################

           Create the user file in the db and initiate the multipart file upload on Aws
         */
        if (this._initFileData == null) {
            //if we have paused it, don't re-fetch the init file data
            this.changeTextStatus(this._config.StaticStrings.StartingUpload);
            this.changeStatus(FileUploadStatus.Initialized);

            let initUploadResponse;
            
            try {
                this._logs.addEntry("Starting the multi-part file upload and creating the user file record");
                
                initUploadResponse = await fetch(this._config.MultiPartFileStartUploadUrl,
                    {
                        method: "POST",
                        headers: {
                            "Content-type": "application/json",
                            "x-auth": this._config.AuthToken
                        },
                        body: JSON.stringify(<apiMultiPartFileInitRequestData>{
                            EndPoint: `https://s3-${this._config.Region}.amazonaws.com/${this._config.Bucket}`,
                            Folder: this._config.Folder,
                            Name: this.File.name(),
                            FileType: this.File.fileType,
                            FileSize: this.File.totalSize,
                            UserLogId: this._userLogId
                        }),
                        signal: this.abortController!.signal
                    });

                if (initUploadResponse.ok) {
                    this._logs.addEntry("User file created");
                    
                    //Get the user file data
                    //Keep in mind that it is not yet uploaded, but here we get upload id, file parts to upload etc.
                    this._initFileData = await initUploadResponse.json() as apiMultiPartFileInitResponseData;

                    if (this._config.Events?.OnFileUploadStarted) {
                        this._config.Events.OnFileUploadStarted(this._initFileData.FileId);
                    }
                } else {
                    const responseText = await initUploadResponse.text();
                    
                    if (responseText.startsWith("{")) {
                        throw new WCCResourcedError(JSON.parse(responseText));
                    }
                    else {
                        this._logs.addEntry(responseText);
                        throw new Error(responseText);
                    }
                }
            } catch (e: any) {
                let responseText;
                if (e instanceof WCCResourcedError) {
                    const resourcedError = e as WCCResourcedError;
                    responseText = resourcedError.toString();
                    this.changeTextStatus(responseText);
                }
                else {
                    responseText = (e as Error).message;
                    this.changeTextStatus(responseText.substring(0, 50) + "....");
                }
                
                this.changeStatus(FileUploadStatus.Failed);
                
                if (this._config.Events?.OnFileUploadFailed && initUploadResponse != null) {
                    this._config.Events.OnFileUploadFailed({ RequestStatus: initUploadResponse.status, ResponseText: responseText, StatusText: initUploadResponse.statusText });
                }

                if (system.isNotNullOrEmpty(this._userLogId)) {
                    await this._userLogsService.updateLogs(this._userLogId, this._logs.GetAllCombinedLogs('\n'), {
                        result: enums.UserLogOperationResultTypes.Failed.value
                    });
                }

                if (this._config.Events?.OnFileUploadComplete) {
                    this._config.Events?.OnFileUploadComplete();
                }
                
                this.changeCanBeDeleted(true);
                return;
            }

            this._logs.addEntry('Going to upload all file chunk(s) now');
        }

        /* ##################
                STEP 2
           #################

           Upload all the chunks of the file to S3
         */
        this.changeTextStatus(this._config.StaticStrings.Uploading);
        this.changeStatus(FileUploadStatus.Uploading);

        // Progress is now a sum of all parts
        this.changeTotalSize(100);
        
        // the fraction/percentage that each part represents
        let partFraction = 100 / this._initFileData.Parts.length;
        let totalLoaded = 0;
        let totalBytesTransferred = 0;
        const speedMeter = new SpeedoMeter();
        let speedLogsAdded: number[] = [];
        
        for (let i = this._currentPartBeingUploaded; i <= this._initFileData.Parts.length; i++) {
            const part = this._initFileData.Parts[i - 1];
            const fileBlob = this.File.fileContents.slice(part.StartPosition, part.EndPosition);
            
            let attemptCount = this._partsUploadAttemptsCount.get(part.PartNumber) ?? 0;
            let partSucceeded = false;
            
            while (attemptCount < UploadToS3MultiPart.MaxRetryCount) {
                //Let's first update the attempt count to rule out any infinite loop
                let partBytesTransferred = 0;
                let partBytesUploadStartTime = performance.now();
                
                ++attemptCount;
                this._partsUploadAttemptsCount.set(part.PartNumber, attemptCount);

                const xhr = new XMLHttpRequest();
                xhr.open('PUT', part.SignedUploadUrl, true);
                xhr.timeout = this._xhrTimeout; // Timeout in milliseconds (30 secs)
                xhr.setRequestHeader('Content-Type', this.File.fileType);
                
                //Hook-in the progress listener so that we can dispatch progress notifications
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        partBytesTransferred = event.loaded;
                        
                        // get the current file progress based on loaded/file size * part fraction e.g. 50/100 * 25 = 12.5%
                        let currentFileProgress = (event.loaded / fileBlob.size) * partFraction;
                        
                        // total loaded is number of parts completed * part fraction + current file progress
                        totalLoaded = partFraction * (i - 1) + currentFileProgress;
                        
                        const totalLoadedStr = totalLoaded.toFixed(0);
                        
                        this.changeTextStatus(`${this._config.StaticStrings.Uploading} ${totalLoadedStr}%`);
                        this.changeUploadedSize(totalLoaded);

                        let totalLoadedInt: number | null = null; 
                        
                        try {
                            totalLoadedInt = parseInt(totalLoadedStr);
                        }
                        catch {
                            //ignore
                        }
                        
                        if (totalLoadedInt != null) {
                            if (this.SpeedReportingProgressIntervals.includes(totalLoadedInt)) { //we need to log speed at this progress interval
                                if (!speedLogsAdded.includes(totalLoadedInt)) { //check if we have not already added speed for this speed interval
                                    this._logs.addEntry(`Upload ${totalLoadedStr}% done with upload speed of ${speedMeter.getSpeedInMbps(totalBytesTransferred + partBytesTransferred)}`);
                                    speedLogsAdded.push(totalLoadedInt);
                                }
                            }
                        }
                        //console.log(`Upload ${totalLoadedStr}% done with upload speed of ${speedMeter.getSpeedInMbps(totalBytesTransferred + partBytesTransferred)}`);
                    }
                });
                
                try {
                    //Wrap each xhr in a promise so that we can leverage async/await nicely
                    await new Promise<void>((resolve, reject) => {
                        xhr.onload = () => {
                            if (xhr.status >= 200 && xhr.status < 300) {
                                const eTag = xhr.getResponseHeader('ETag');
                                this._eTags.push(`${part.PartNumber}:${(eTag ?? "")}`);

                                if (this._config.Events?.OnFileCunkUploaded) {
                                    this._config.Events?.OnFileCunkUploaded(part);
                                }

                                partSucceeded = true;
                                this._partsUploadAttemptsCount.delete(part.PartNumber); //if this is successful on 2nd or 3rd attempt, remove it from the attempts set
                                totalBytesTransferred += partBytesTransferred;

                                resolve();
                            } else {
                                reject(new Error(`File Chunk #${part.PartNumber} failed to upload. ${this._config.StaticStrings.TheRemoteEndpointReturnedAnError(xhr)}`));
                            }
                        };

                        xhr.onerror = () => reject(new Error(`OnError: File Chunk #${part.PartNumber} failed to upload. ${this._config.StaticStrings.TheRemoteEndpointReturnedAnError(xhr)}`));
                        xhr.ontimeout = () => reject(new UploadTimeoutError(this._config.StaticStrings.TheRemoteEndpointReturnedAnError(xhr)));

                        //Handle cancellation
                        this.abortController!.signal.addEventListener('abort', () => {
                            xhr.abort();
                            this.abortController = null;
                            reject(new UploadCancelledError(this._config.StaticStrings.UploadCancelled));
                        });

                        this.abortController!.signal.addEventListener('pause', () => {
                            xhr.abort();
                            this.abortController = null;
                            reject(new UploadPausedError(this._config.StaticStrings.UploadPaused));
                        });

                        // Send the request with the file blob
                        xhr.send(fileBlob);
                    });
                    break;
                } catch (e: any) {
                    if (totalBytesTransferred >= partBytesTransferred) {
                        totalBytesTransferred -= partBytesTransferred;
                    }
                    
                    //since all the bytes of this part are going to be uploaded again, don't count the time of this iteration as well
                    speedMeter.addToOffset(performance.now() - partBytesUploadStartTime)
                    partBytesTransferred = 0;
                    
                    if (e instanceof UploadPausedError) {
                        const pausedError = e as UploadPausedError;

                        //now reset everything
                        this.changeTextStatus(pausedError.message);
                        this.changeTotalSize(0);
                        this.changeUploadedSize(0);

                        //Since we update the attempt count in the beginning just to be super safe, we want to preserve the current count
                        //if the upload is paused, hence decrement by 1 to keep it at current count 
                        this._partsUploadAttemptsCount.set(part.PartNumber, attemptCount - 1);
                        
                        break; //break the retry loop
                    }
                    else if (e instanceof UploadCancelledError) {
                        const cancelledError = e as UploadCancelledError;
                        
                        //now reset everything
                        this.changeStatus(FileUploadStatus.Cancelled);
                        this.changeTextStatus(cancelledError.message);

                        this.changeTotalSize(0);
                        this.changeUploadedSize(0);
                        break; //break the retry loop
                    }
                    else {
                        if (e instanceof UploadTimeoutError) {
                            this._timedOutCount++;

                            if (attemptCount == UploadToS3MultiPart.MaxRetryCount) {
                                this._logs.addEntry(e.message);
                            }
                        }
                        else {
                            this._logs.addEntry(e.message);
                        }

                        if (attemptCount < UploadToS3MultiPart.MaxRetryCount) {
                            const retryIntervalInSecs = attemptCount * UploadToS3MultiPart.RetryIntervalInSecs; //1st time - 10 secs, 2nd time - 20 secs
                            const retryIntervalInMs = retryIntervalInSecs * 1000;
                            
                            speedMeter.addToOffset(retryIntervalInMs); //don't count the wait time in speed calculation
                            
                            this._logs.addEntry(`File part: ${part.PartNumber}, Current retry count: ${attemptCount + 1}. Going to retry in ${retryIntervalInSecs} secs..`);
                            
                            const retryMsg = this._config.StaticStrings.UploadError(retryIntervalInSecs)
                            this.changeTextStatus(retryMsg);
                            this.isWaitingToRetry(true);
                            
                            await new Promise<void>((resolve, _) => {
                                setTimeout(() => {
                                    this.isWaitingToRetry(false);
                                    resolve();
                                }, retryIntervalInMs);
                            });
                        }
                        
                        if (attemptCount == UploadToS3MultiPart.MaxRetryCount) {
                            this.changeStatus(FileUploadStatus.Failed);
                            this.changeTextStatus(xhr.status == 0 ? this._config.StaticStrings.NetworkError : this.getErrorMessageFromResponse(xhr));
                            
                            if (this._config.Events?.OnFileUploadFailed) {
                                this._config.Events?.OnFileUploadFailed({
                                    RequestStatus: xhr.status,
                                    StatusText: xhr.statusText,
                                    ResponseText: xhr.responseText
                                });
                            }
                        }
                    }
                }
            }
            
            this._currentPartBeingUploaded = i;

            if (this.isPaused() || this.status() == FileUploadStatus.Cancelled) {
                break; //if paused or cancelled, break the outer loop
            }
            
            if (!partSucceeded) { //tried max number of times, mark this part as failed and bail out
                this._logs.addEntry("Re-tried uploading file max number of times..");
                this.changeTextStatus(this._config.StaticStrings.NetworkError);
                this._partsUploadAttemptsCount.delete(part.PartNumber);

                break; //if tried max number of times, break the outer loop and exit
            }
        }
        
        if (this.isPaused() || this.status() == FileUploadStatus.Cancelled || this.status() == FileUploadStatus.Failed) {
            if (!this.isPaused()) {
                if (this.status() == FileUploadStatus.Cancelled || this.status() == FileUploadStatus.Failed) {
                    if (this._timedOutCount > 0) {
                        this._logs.addEntry(`Total timeouts: ${this._timedOutCount}`);
                    }

                    if (this._pausedCount > 0) {
                        this._logs.addEntry(`Upload paused/resumed: ${this._pausedCount} time(s)`);
                    }
                }
                
                if (this.status() == FileUploadStatus.Failed) {
                    this._logs.addEntry('File failed to upload');
                }

                if (this.status() == FileUploadStatus.Cancelled) {
                    this._logs.addEntry('File upload cancelled');
                }

                if (system.isNotNullOrEmpty(this._userLogId)) { //don't submit logs on pause
                    await this._userLogsService.updateLogs(this._userLogId, this._logs.GetAllCombinedLogs('\n'), {
                        result: this.status() == FileUploadStatus.Cancelled ? enums.UserLogOperationResultTypes.Cancelled.value : enums.UserLogOperationResultTypes.Failed.value
                    });
                }

                if (this._config.Events?.OnFileUploadComplete) {
                    this._config.Events?.OnFileUploadComplete();
                }
            }
            
            if (this.status() == FileUploadStatus.Cancelled || this.status() == FileUploadStatus.Failed) {
                this.changeCanBeDeleted(true);
            }
            return;
        }
        

        /* ##################
                STEP 3
           #################

           Marks the upload as finished/complete
         */

        if (this._timedOutCount > 0) {
            this._logs.addEntry(`Total timeouts: ${this._timedOutCount}`);
        }

        if (this._pausedCount > 0) {
            this._logs.addEntry(`Upload paused/resumed: ${this._pausedCount} time(s)`);
        }
        
        this._logs.addEntry("All file chunks uploaded, now marking the upload as complete");
        
        let finishUploadResponse;
        
        try {
            //Let's now mark the file upload as completed
            finishUploadResponse = await fetch(this._config.MultiPartFileFinishUploadUrl,
                {
                    method: "POST",
                    headers: {
                        "Content-type": "application/json",
                        "x-auth": this._config.AuthToken
                    },
                    body: JSON.stringify(<apiMultiPartFileCompleteRequestData>{
                        FileId: this._initFileData.FileId,
                        ChunkIds: this._eTags,
                        UploadId: this._initFileData.UploadId,
                        UserLogId: this._userLogId
                    }),
                    signal: this.abortController!.signal
                });

            if (finishUploadResponse.ok) {
                //Get final data
                let multiPartFileData = await finishUploadResponse.json() as apiMultiPartFileCompleteResponseData;

                this._logs.addEntry("File Uploaded Successfully");
                
                if (system.isNotNullOrEmpty(this._userLogId)) {
                    await this._userLogsService.updateLogs(this._userLogId, this._logs.GetAllCombinedLogs('\n'), {
                        result: enums.UserLogOperationResultTypes.Success.value
                    });
                }

                //Inform the user that we're done
                this.changeStatus(FileUploadStatus.Uploaded);
                this.changeTextStatus(this._config.StaticStrings.UploadedSuccessfully);

                this.changeCanBeDeleted(true);
                
                //Trigger external events
                if (this._config.Events?.OnFileUploaded) {
                    this._config.Events?.OnFileUploaded(<UploadToS3Result>{
                        FileId: multiPartFileData.FileId,
                        FileUrl: multiPartFileData.SignedS3Link
                    })
                }
                
                if (this._config.Events?.OnFileUploadComplete) {
                    this._config.Events?.OnFileUploadComplete();
                }
            }
            else {
                const responseText = await finishUploadResponse.text();

                if (responseText.startsWith("{")) {
                    throw new WCCResourcedError(JSON.parse(responseText));
                }
                else {
                    this._logs.addEntry(responseText);
                    throw new Error(responseText);
                }
            }
        } catch (e: any) {
            let responseText;
            
            if (e instanceof WCCResourcedError) {
                const resourcedError = e as WCCResourcedError;
                responseText = resourcedError.toString();
                this.changeTextStatus(responseText);
            }
            else {
                responseText = (e as Error).message;
                this.changeTextStatus(responseText.substring(0, 50) + "....");
            }

            this._logs.addEntry(`There was an issue completing the upload. Issue Details: ${responseText}`);

            this.changeStatus(FileUploadStatus.Failed);
            this.changeCanBeDeleted(true);

            if (this._config.Events?.OnFileUploadFailed && finishUploadResponse != null) {
                this._config.Events.OnFileUploadFailed({ RequestStatus: finishUploadResponse.status, ResponseText: responseText, StatusText: finishUploadResponse.statusText });
            }

            if (system.isNotNullOrEmpty(this._userLogId)) {
                await this._userLogsService.updateLogs(this._userLogId, this._logs.GetAllCombinedLogs('\n'), {
                    result: enums.UserLogOperationResultTypes.Failed.value
                });
            }

            if (this._config.Events?.OnFileUploadComplete) {
                this._config.Events?.OnFileUploadComplete();
            }
            
            return;
        }
    }
    
    /**
     * Pauses the upload operation if pause/resume is supported
     */
    override pauseUpload() {
        if (this.isWaitingToRetry()) return;
        
        this._pausedCount++;
        super.pauseUpload();
        this.abortController?.signal.dispatchEvent(new Event("pause"));
    }

    /**
     * Resumes a paused upload operation if pause/resume is supported
     */
    override async resumeUpload() {
        if (this.isWaitingToRetry()) return;
        
        super.resumeUpload();
        await this.upload();
    }

    override cancelUpload() {
        this.changeStatus(FileUploadStatus.Cancelled);
        super.cancelUpload();
    }

    /**
     * Restarts the upload
     */
    override async restartUpload(): Promise<void> {
        if (this.isWaitingToRetry()) return;
        
        this._currentPartBeingUploaded = 1;
        this._initFileData = undefined;
        this._eTags = [];
        this._partsUploadAttemptsCount.clear();
        this._userLogId = "";
        
        if (this._logsCountBeforeUpload > 0) {
            this._logs.clearLogsFrom(this._logsCountBeforeUpload);
        }
        
        this._logs.addEntry("Resuming cancelled file..")
        await super.restartUpload();
    }

    /**
     * Parses the response from XHR, looks for <message> if it's an XML response and extracts the message
     * Otherwise it returns the statusText on XHR 
     * @param xhr Http Request
     * @private
     */
    private getErrorMessageFromResponse(xhr: XMLHttpRequest) {
        let message = xhr.statusText;

        let messageRegexResult = /<message>(.*?)<\/message>/i.exec(xhr.responseText);
        if (messageRegexResult != null && messageRegexResult.length > 0) {
            message = messageRegexResult[0].replace(/<message>/i, "").replace(/<\/message>/i, "");
        }
        
        return message;
    }
}